import React from "react";
import { useNavigate } from "react-router-dom";
import Pagination from "../../ui/Pagination/Pagination";
import tableattachmenticon from "../../../assets/images/tableattachmenticon.svg";
import avtar from "../../../assets/images/defaultavtarimg.svg";
import Badge from 'react-bootstrap/Badge';
import { useAuth } from "../../../firebase/AuthProvider";
import { faBookmark, faBug, faCode, faSort } from "@fortawesome/free-solid-svg-icons";
import SkeletonRow from "../../ui/LoadingSkeleton/SkeletonRow";
import Ticket from "../../../interfaces/ticket/Ticket";
import { Table } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";


interface IssuesPageTableProps {
    tickets: Ticket[];
    displayTicketCount: boolean;
    page: number;
    pageSize: number;
    totalCount: number;
    isLoading: boolean;
    onPageChange?: (page: number) => void;
    setPage: (page: number) => void;
    setPageSize: (size: number) => void;
    handleSort?: (columnName: string) => void;
    onTicketClick: (ticketId: string) => void;
}

const IssuesPageTable: React.FC<IssuesPageTableProps> = ({
    tickets,
    displayTicketCount = false,
    page,
    pageSize,
    totalCount,
    isLoading,
    onPageChange,
    setPage,
    setPageSize,
    handleSort,
    onTicketClick,
}) => {
    const navigate = useNavigate();
    const { currentUser, currentOrganization } = useAuth();

    const resetPageSize = (pageNumber: number, pageSize: number) => {
        setPageSize(pageSize);
        setPage(pageNumber);
    }

    const isLate = (dueDate: Date): string => {
        try {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const checkDate = new Date(dueDate);
            checkDate.setHours(0, 0, 0, 0);
            if (checkDate < today) {
                return 'red-text'
            } else {
                return ''
            }
        } catch {
            return '';
        }
    };

    return (
        <>
            <Table responsive className="common_table" style={{ tableLayout: 'fixed' }}>
                <thead>
                    <tr>
                        <th style={{ width: '35%' }}>Title</th>
                        <th style={{ width: '10%' }}>Attachment</th>
                        <th style={{ width: '15%' }}>Assignee</th>
                        <th style={{ width: '15%' }}>Due Date  <FontAwesomeIcon className="cursor-pointer" icon={faSort} onClick={() => handleSort?.("enddate")} /></th>
                        <th style={{ width: '15%' }}>Sprint</th>
                        <th style={{ width: '10%' }}>Status  <FontAwesomeIcon className="cursor-pointer" icon={faSort} onClick={() => handleSort?.("status")} /></th>
                    </tr>
                </thead>
                <tbody>
                    {isLoading ? (
                        [...Array(pageSize)].map((_, index) => <SkeletonRow key={index} columns={6} />)
                    ) : tickets?.length > 0 ? (
                        tickets.map((ticket, index) => (
                            <tr key={ticket.id || index} onClick={() => onTicketClick(ticket.publicId)} style={{ cursor: 'pointer' }}>
                                <td title={ticket.title} style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} className="flex justify-start align-middle items-center gap-2">
                                    {ticket.tags?.some(tag => tag.name.toLowerCase() === "bug") && (<FontAwesomeIcon className="w-3 text-red-500" icon={faBug} />)}
                                    {ticket.tags?.some(tag => tag.name.toLowerCase() === "story") && (<FontAwesomeIcon className="w-3 text-green-500" icon={faBookmark} />)}
                                    {ticket.tags?.some(tag => tag.name.toLowerCase() === "task") && (<FontAwesomeIcon className="w-3 text-blue-500" icon={faCode} />)}
                                    <span className="highlight_txt">{ticket.title}</span>
                                </td>
                                <td>
                                    {ticket.hasAttachments ? (
                                        <img src={tableattachmenticon} alt="attachment" />
                                    ) : (
                                        "-"
                                    )}
                                </td>
                                <td>
                                    <div className="stack_img_flx">
                                        {ticket.assignee ? (
                                            <div className="stack_img" title={ticket.assignee.displayName}>
                                                {ticket.assignee.avatar ? (
                                                    <img
                                                        className="stack_img "
                                                        src={ticket.assignee.avatar || avtar}
                                                        alt={ticket.assignee.displayName}
                                                        onError={(e) => {
                                                            const target = e.target as HTMLImageElement;
                                                            target.onerror = null;
                                                            target.src = avtar;
                                                        }}
                                                    />
                                                ) : (
                                                    <div className="stack_img_txt">
                                                        {ticket.assignee.displayName.split(' ').map(n => n[0]).join('')}
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            "-"
                                        )}
                                    </div>
                                </td>
                                <td className={`${ticket.endDate ? isLate(ticket.endDate) : "--"}`}>
                                    {ticket.endDate
                                        ? new Date(ticket.endDate).toLocaleDateString("en-US")
                                        : "--"}
                                </td>
                                <td>{ticket.sprint ? ticket.sprint.name : "Backlog"}</td>
                                <td>
                                    <Badge
                                        className={`${(ticket.status === 'Open' || ticket.status === 'In Progress') && 'text-black'}`}
                                        bg={
                                            ticket.status === "Done" || ticket.status === "Awaiting Release"
                                                ? "success"
                                                : ticket.status === "Testing"
                                                    ? "warning"
                                                    : ticket.status === "Open" || ticket.status === 'In Progress'
                                                        ? "light"
                                                        : ticket.status === 'CLOSED' || ticket.status?.toLowerCase().includes('issues')
                                                            ? "danger"
                                                            : "secondary"
                                        }
                                    >
                                        {ticket.status || "-"}
                                    </Badge>
                                </td>
                            </tr>
                        ))
                    ) : (
                        <tr>
                            <td className="text-center" colSpan={6}>
                                No tickets available
                            </td>
                        </tr>
                    )}
                </tbody>
            </Table>


            {onPageChange ? (<>
                {totalCount > 0 && <Pagination
                    currentPage={page}
                    totalPages={Math.ceil(totalCount / pageSize) || 0}
                    onPageChange={onPageChange}
                    pageSize={pageSize}
                    resetPageSize={resetPageSize}
                />}
            </>
            ) : (
                <div className="text-muted mx-auto text-end pe-8 pb-3 pt-0 text-sm w-full">
                    {"Page: " + page} |{" "}
                    {totalCount + " total tickets"}
                </div>
            )}
        </>
    );
};

export default IssuesPageTable;
